import React, { useEffect, useState, useCallback, useRef } from "react";
import Navbar from "@/components/Navbar";
import MapView from "@/components/MapView";
import { useAuth } from "@/lib/auth";
import { api, GEORGETOWN, formatGYD } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Car,
  Path,
  MapPin,
  Money,
  CheckCircle,
  Play,
  XCircle,
  Lightning,
  Crosshair,
  WifiHigh,
  Phone,
  ShieldCheck,
  WheelchairMotion,
  Warning,
  WhatsappLogo,
  SpeakerHigh,
  SpeakerSlash,
  Star,
  BellRinging,
  BellSlash,
} from "@phosphor-icons/react";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useSpeedWarning } from "@/hooks/useSpeedWarning";
import SpeedWarningModal from "@/components/SpeedWarningModal";
import { useNativeGPS } from "@/hooks/useNativeGPS";
import DriverAppBanner from "@/components/DriverAppBanner";

export default function DriverDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [online, setOnline] = useState(false);
  const [loc, setLoc] = useState(GEORGETOWN);
  const [requests, setRequests] = useState([]);
  const [activeRide, setActiveRide] = useState(null);
  const [gpsStatus, setGpsStatus] = useState("idle"); // idle | watching | denied | unsupported
  const [lastSync, setLastSync] = useState(null);
  // Live device-reported speed (km/h) and heading (deg) for the self-marker
  // bubble. Updated from `navigator.geolocation.watchPosition`.
  const [selfSpeed, setSelfSpeed] = useState(null);
  const [selfHeading, setSelfHeading] = useState(null);
  // Breadcrumb trail: last ~10 positions for the self-marker. We keep this in
  // state (not a ref) so MapView re-renders the polyline as the driver moves.
  const [selfTrail, setSelfTrail] = useState([]);
  const [communityPins, setCommunityPins] = useState([]);

  // Pull community pins every 60s so the driver sees nearby cops / hazards /
  // events while driving — same data as the Ride Community page.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/community/posts?limit=80");
        if (!cancelled) setCommunityPins(data || []);
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  const [nowTick, setNowTick] = useState(Date.now());
  // Tick every second so the "GPS · Xs ago" counter actually updates live.
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const [broadcast, setBroadcast] = useState(true);
  const [broadcastSpeed, setBroadcastSpeed] = useState(true);
  const [occupancy, setOccupancy] = useState(null); // "empty" | "1"..."4" | "full" | null
  const [soundOn, setSoundOn] = useState(true);
  const push = usePushNotifications();
  const watchIdRef = useRef(null);
  const lastSentRef = useRef({ lat: null, lng: null, t: 0 });
  const knownReqIdsRef = useRef(new Set());
  const audioCtxRef = useRef(null);

  // Lazily build / resume an AudioContext. Browsers require a user gesture
  // (toggle online) before audio can play — we prime it there.
  const primeAudio = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) audioCtxRef.current = new Ctx();
      }
      if (audioCtxRef.current?.state === "suspended") audioCtxRef.current.resume();
    } catch (_e) { /* unsupported browser */ }
  }, []);

  // Play a 3-beep "incoming ride" tone. Repeats up to 3 cycles so the driver
  // can hear it even if their phone is in a pocket.
  const playRingtone = useCallback(() => {
    if (!soundOn) return;
    primeAudio();
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    const now = ctx.currentTime;
    // 3 alternating beeps × 3 cycles, total ≈ 4.5 s
    const pattern = [
      { f: 880, t: 0.0, d: 0.18 },
      { f: 660, t: 0.22, d: 0.18 },
      { f: 880, t: 0.44, d: 0.22 },
    ];
    for (let cycle = 0; cycle < 3; cycle++) {
      const base = now + cycle * 1.5;
      pattern.forEach(({ f, t, d }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = f;
        osc.type = "sine";
        gain.gain.setValueAtTime(0.0001, base + t);
        gain.gain.exponentialRampToValueAtTime(0.35, base + t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, base + t + d);
        osc.connect(gain).connect(ctx.destination);
        osc.start(base + t);
        osc.stop(base + t + d + 0.02);
      });
    }
    // Best-effort haptic buzz on Android — iOS Safari ignores this.
    if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 200]);
  }, [soundOn, primeAudio]);

  const loadProfile = useCallback(async () => {
    try {
      const { data } = await api.get("/drivers/me");
      setProfile(data);
      setOnline(!!data.online);
      setBroadcast(data.broadcast_location !== false);
      setBroadcastSpeed(data.broadcast_speed !== false);
      setOccupancy(data.occupancy || null);
      if (data.location) setLoc(data.location);
    } catch { /* no-op */ }
  }, []);

  // Pull a lightweight session token that we hand to the native
  // RideGYTracker plugin so its ForegroundService can POST /drivers/location
  // with the correct Bearer even when the WebView is suspended.
  const [nativeSessionToken, setNativeSessionToken] = useState(null);
  useEffect(() => {
    if (!window?.Capacitor?.isNativePlatform) return;
    api.get("/auth/mobile-token")
      .then(({ data }) => setNativeSessionToken(data?.token || null))
      .catch(() => { /* endpoint may not exist on older backends */ });
  }, []);
  const nativeGPS = useNativeGPS({ sessionToken: nativeSessionToken });

  // Cold-start guard: if the driver opens the app while already marked
  // online in the DB, ask for GPS permission so both the native
  // foreground service AND the WebView's watchPosition can start.
  useEffect(() => {
    if (!nativeGPS.isNative) return;
    if (!online) return;
    if (nativeGPS.tracking) return;
    const t = setTimeout(() => {
      if (nativeGPS.tracking) return;
      nativeGPS.startTracking().then((res) => {
        if (!res.ok && res.reason === "permission_denied") {
          toast.error("Grant location permission so riders can see you.");
        }
      }).catch(() => { /* swallowed — never blocks the UI */ });
    }, 1000);
    return () => clearTimeout(t);
  }, [online, nativeGPS]);

  const loadActive = useCallback(async () => {
    try {
      const { data } = await api.get("/rides/active");
      setActiveRide(data || null);
    } catch {}
  }, []);

  const loadRequests = useCallback(async () => {
    try {
      const { data } = await api.get("/driver/requests");
      // Detect ride request IDs we haven't ringed for yet → ring the driver.
      // We ALWAYS ring on first-sight, including the first poll after going
      // online — drivers explicitly want to hear pending pings they walked in
      // on, not just brand-new ones.
      const incoming = (data || []).map((r) => r.ride_id);
      const fresh = incoming.filter((id) => !knownReqIdsRef.current.has(id));
      if (fresh.length > 0) {
        playRingtone();
        toast.success(`${fresh.length} ride request${fresh.length > 1 ? "s" : ""} waiting`, {
          description: "Tap Accept to claim it.",
        });
      }
      knownReqIdsRef.current = new Set(incoming);
      setRequests(data);
    } catch (_e) { /* network blip — next tick retries */ }
  }, [playRingtone]);

  useEffect(() => {
    loadProfile();
    loadActive();
  }, [loadProfile, loadActive]);

  useEffect(() => {
    if (!online) { setRequests([]); return; }
    loadRequests();
    const t = setInterval(() => {
      loadRequests();
      loadActive();
    }, 4000);
    return () => clearInterval(t);
  }, [online, loadRequests, loadActive]);

  // -------- Real-time GPS watch when online --------
  const pushLocation = useCallback(async (lat, lng, speed_mps = null, heading = null, accuracy_m = null) => {
    try {
      await api.post("/drivers/location", { lat, lng, speed_mps, heading, accuracy_m });
      setLastSync(new Date());
    } catch (_e) { /* network blip — next tick retries */ }
  }, []);

  // Keep the screen alive while driving so the OS doesn't suspend the tab.
  const wakeLockRef = useRef(null);
  const requestWakeLock = useCallback(async () => {
    if (!("wakeLock" in navigator)) return;
    try {
      wakeLockRef.current = await navigator.wakeLock.request("screen");
      wakeLockRef.current.addEventListener("release", () => {
        wakeLockRef.current = null;
      });
    } catch (_e) { /* user/UA refused — non-fatal */ }
  }, []);
  const releaseWakeLock = useCallback(async () => {
    try { await wakeLockRef.current?.release(); } catch (_e) { /* ignore */ }
    wakeLockRef.current = null;
  }, []);

  // Re-acquire wake lock + force a fresh GPS sample whenever the tab returns
  // to the foreground (Android/iOS auto-release wake-lock on hide).
  useEffect(() => {
    const onVis = () => {
      // Report visibility to backend — if driver has an active ride and goes
      // hidden, backend schedules a reminder push ~20s later.
      const hasActive = !!activeRide;
      api.post("/push/visibility", {
        visibility: document.visibilityState,
        has_active_ride: hasActive,
        ride_id: activeRide?.id,
      }).catch(() => {});
      if (document.visibilityState === "visible" && online) {
        requestWakeLock();
        if ("geolocation" in navigator) {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              const sp = pos.coords.speed;
              const hd = pos.coords.heading;
              const acc = pos.coords.accuracy;
              pushLocation(
                pos.coords.latitude,
                pos.coords.longitude,
                (typeof sp === "number" && !Number.isNaN(sp) && sp >= 0) ? sp : null,
                (typeof hd === "number" && !Number.isNaN(hd)) ? hd : null,
                (typeof acc === "number" && acc >= 0) ? acc : null,
              );
            },
            () => {},
            { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
          );
        }
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [online, activeRide, requestWakeLock, pushLocation]);

  useEffect(() => {
    // Stop watch when going offline
    if (!online) {
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      setGpsStatus("idle");
      setSelfTrail([]);
      setSelfSpeed(null);
      setSelfHeading(null);
      releaseWakeLock();
      return;
    }
    if (!("geolocation" in navigator)) {
      setGpsStatus("unsupported");
      toast.error("GPS not supported by this browser");
      return;
    }
    setGpsStatus("watching");
    requestWakeLock();
    toast.message("Keep RideGY in front", {
      description: "Lock your phone or switch tabs and the map will freeze until you come back.",
      duration: 6000,
    });
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude: lat, longitude: lng, speed, heading } = pos.coords;
        setLoc({ lat, lng });
        // Append to breadcrumb trail (only if moved > ~5m from last point,
        // capped at 12 points so the polyline stays light on render).
        setSelfTrail((prev) => {
          const last = prev[prev.length - 1];
          if (last) {
            const dy = (lat - last[0]) * 111320;
            const dx = (lng - last[1]) * 111320 * Math.cos((lat * Math.PI) / 180);
            if (Math.sqrt(dx * dx + dy * dy) < 5) return prev;
          }
          const next = [...prev, [lat, lng]];
          return next.length > 12 ? next.slice(next.length - 12) : next;
        });
        // Live UI: update the self-bubble immediately on every fix so the
        // driver sees movement & speed without waiting for the throttled
        // server push.
        const speedKmhLive = (typeof speed === "number" && !Number.isNaN(speed) && speed >= 0)
          ? speed * 3.6
          : null;
        if (speedKmhLive != null) setSelfSpeed(speedKmhLive);
        if (typeof heading === "number" && !Number.isNaN(heading)) setSelfHeading(heading);
        // Throttle: send only if moved > 15m OR > 5s since last send
        const last = lastSentRef.current;
        const dt = Date.now() - last.t;
        let dist = Infinity;
        if (last.lat != null && last.lng != null) {
          const dy = (lat - last.lat) * 111320;
          const dx = (lng - last.lng) * 111320 * Math.cos((lat * Math.PI) / 180);
          dist = Math.sqrt(dx * dx + dy * dy);
        }
        if (dt > 5000 || dist > 15) {
          lastSentRef.current = { lat, lng, t: Date.now() };
          // pos.coords.speed is in m/s on supported devices (null on some browsers)
          const speedMps = (typeof speed === "number" && !Number.isNaN(speed) && speed >= 0) ? speed : null;
          const hdg = (typeof heading === "number" && !Number.isNaN(heading)) ? heading : null;
          const acc = (typeof pos.coords.accuracy === "number" && pos.coords.accuracy >= 0)
            ? pos.coords.accuracy : null;
          pushLocation(lat, lng, speedMps, hdg, acc);
        }
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setGpsStatus("denied");
          toast.error("Allow location access for real-time tracking");
        } else {
          setGpsStatus("error");
        }
      },
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 }
    );
    watchIdRef.current = id;
    return () => {
      if (navigator.geolocation && id != null) navigator.geolocation.clearWatch(id);
      watchIdRef.current = null;
    };
  }, [online, pushLocation]);

  const toggleOnline = async (val) => {
    setOnline(val);
    if (val) {
      primeAudio();  // user gesture → audio context can now play
      knownReqIdsRef.current = new Set();  // fresh queue on each online cycle
    } else {
      knownReqIdsRef.current = new Set();
    }
    try {
      await api.post("/drivers/online", { online: val, lat: loc.lat, lng: loc.lng });
      toast.success(val ? "You're online" : "You're offline");
    } catch (e) {
      toast.error("Failed to toggle");
      setOnline(!val);
      return;
    }
    // When running inside the Android wrapper, ALSO start/stop the
    // native background tracker. This is what keeps GPS pings flowing
    // when the phone locks — the whole reason the app exists. Runs
    // AFTER the API toggle succeeds and is fully guarded: no native
    // plugin failure can flip the online state back or crash the flow.
    if (nativeGPS.isNative) {
      try {
        const res = val ? await nativeGPS.startTracking() : await nativeGPS.stopTracking();
        if (val && res && !res.ok && res.reason === "permission_denied") {
          toast.error("Grant background location so tracking survives phone lock.");
        }
      } catch (err) {
        // Native plugin blew up. The web-side geolocation watch already
        // covers the foreground case — user is still online. Log & move on.
        console.warn("[DriverDashboard] native tracker toggle failed:", err?.message || err);
      }
    }
  };

  const toggleBroadcast = async (val) => {
    setBroadcast(val);
    try {
      await api.post("/drivers/broadcast", { broadcast: val });
      toast.success(val ? "Broadcasting to landing map" : "Hidden from landing map");
    } catch (e) {
      setBroadcast(!val);
      toast.error("Failed to update");
    }
  };

  const toggleBroadcastSpeed = async (val) => {
    setBroadcastSpeed(val);
    try {
      await api.post("/drivers/broadcast-speed", { broadcast: val });
      toast.success(val ? "Speed visible on map" : "Speed hidden from map");
    } catch (e) {
      setBroadcastSpeed(!val);
      toast.error("Failed to update");
    }
  };

  const updateOccupancy = async (val) => {
    // val = null | "empty" | "1" | "2" | "3" | "4" | "full"
    const next = occupancy === val ? null : val;
    const prev = occupancy;
    setOccupancy(next);
    try {
      await api.post("/drivers/occupancy", { occupancy: next });
      if (next === null) toast.success("Occupancy cleared");
      else if (next === "empty") toast.success("Showing EMPTY on map");
      else if (next === "full") toast.success("Showing FULL on map");
      else toast.success(`Showing ${next} passenger${next === "1" ? "" : "s"} on map`);
    } catch (e) {
      setOccupancy(prev);
      toast.error("Failed to update");
    }
  };

  const setMyLocation = async (pt) => {
    setLoc(pt);
    try { await api.post("/drivers/location", { lat: pt.lat, lng: pt.lng }); } catch {}
  };

  const accept = async (rid) => {
    try {
      const { data } = await api.post(`/rides/${rid}/accept`);
      setActiveRide(data);
      toast.success("Ride accepted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const start = async () => {
    try { const { data } = await api.post(`/rides/${activeRide.ride_id}/start`); setActiveRide(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const complete = async () => {
    try {
      await api.post(`/rides/${activeRide.ride_id}/complete`);
      toast.success("Trip completed · collect cash");
      setActiveRide(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const cancel = async () => {
    if (!activeRide) return;
    try {
      await api.post(`/rides/${activeRide.ride_id}/cancel`);
      setActiveRide(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Speed warning — fires at 110 km/h to give the driver a heads-up
  // BEFORE the backend's 120 km/h public safety alert triggers. MUST be
  // called before any conditional early-return so hook order stays stable.
  const speedWarning = useSpeedWarning(selfSpeed);

  if (!profile) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar active="drive" />
        <div className="p-8 font-mono text-sm uppercase tracking-wider">Loading driver profile…</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      <SpeedWarningModal
        open={speedWarning.active}
        peakKmh={speedWarning.peakKmh}
        onDismiss={speedWarning.dismiss}
      />
      <Navbar active="drive" />

      {/* Android-app install nudge (only shows for non-native Android browsers) */}
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 pt-3">
        <DriverAppBanner />
      </div>

      {/* Online bar */}
      <div className={`border-b-2 border-foreground ${online ? "bg-accent text-white" : "bg-secondary"}`}>
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 min-h-[56px] sm:h-16 flex items-center justify-between gap-2 py-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Lightning size={22} weight="fill" className="shrink-0" />
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-70">Status</div>
              <div className="font-display text-base sm:text-2xl truncate" data-testid="driver-status-label">
                {online ? "Online · Accepting" : "Offline"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {online && (() => {
              const secsAgo = lastSync ? Math.max(0, Math.round((nowTick - lastSync.getTime()) / 1000)) : null;
              const stalled = secsAgo != null && secsAgo > 30;
              const warning = gpsStatus === "denied" || gpsStatus === "unsupported" || gpsStatus === "error" || stalled;
              return (
                <div
                  data-testid="driver-gps-indicator"
                  className={`flex items-center gap-1.5 px-2 py-1 sm:px-3 sm:py-1.5 border-2 font-mono text-[9px] sm:text-[10px] uppercase tracking-wider ${warning ? "border-destructive text-destructive bg-destructive/10" : "border-current"}`}
                  title={lastSync ? `Last sync ${lastSync.toLocaleTimeString()}` : "Awaiting GPS fix"}
                >
                  <Crosshair size={12} weight={gpsStatus === "watching" && !stalled ? "fill" : "regular"} />
                  {gpsStatus === "watching" && secsAgo != null && (
                    <>{stalled ? "Stalled " : ""}{secsAgo}s</>
                  )}
                  {gpsStatus === "watching" && secsAgo == null && <>Acquiring…</>}
                  {gpsStatus === "denied" && <>Denied</>}
                  {gpsStatus === "unsupported" && <>Unsupported</>}
                  {gpsStatus === "error" && <>Error</>}
                </div>
              );
            })()}
            <span className="hidden sm:inline font-mono text-xs uppercase tracking-wider">Toggle</span>
            <Switch
              data-testid="driver-online-toggle"
              checked={online}
              onCheckedChange={toggleOnline}
              className="data-[state=checked]:bg-foreground"
            />
          </div>
        </div>
      </div>

      <OccupancyBar occupancy={occupancy} onChange={updateOccupancy} />

      {online && push.supported && !push.subscribed && (
        <div
          className="border-b-2 border-foreground bg-secondary px-3 sm:px-6 py-2 flex items-center gap-3"
          data-testid="driver-push-banner"
        >
          <BellRinging size={18} weight="fill" className="shrink-0 text-accent" />
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">
              Background alerts
            </div>
            <div className="text-sm truncate">
              Get a ping if you accidentally leave RideGY, so GPS never stops.
            </div>
          </div>
          <Button
            data-testid="driver-push-enable"
            onClick={async () => {
              const ok = await push.enable();
              if (ok) toast.success("Notifications enabled");
              else if (push.error) toast.error(push.error);
            }}
            disabled={push.busy}
            className="h-9 rounded-none border-2 border-foreground bg-foreground text-background hover:bg-foreground/90"
          >
            {push.busy ? "…" : "Enable"}
          </Button>
        </div>
      )}
      {online && push.subscribed && (
        <div
          className="border-b-2 border-foreground bg-background px-3 sm:px-6 py-1.5 flex items-center justify-between"
          data-testid="driver-push-status"
        >
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-foreground/60">
            <BellRinging size={12} weight="fill" className="text-accent" />
            Background alerts on
          </div>
          <button
            type="button"
            onClick={() => push.disable()}
            data-testid="driver-push-disable"
            className="font-mono text-[10px] uppercase tracking-wider text-foreground/60 hover:text-foreground inline-flex items-center gap-1"
            title="Turn off background alerts"
          >
            <BellSlash size={12} /> Off
          </button>
        </div>
      )}

      <div className="flex-1 grid grid-cols-1 grid-rows-[55vh_1fr] md:grid-rows-1 md:grid-cols-[1fr_400px] min-h-0">
        {/* Map */}
        <div className="relative min-h-0 border-b-2 md:border-b-0 md:border-r-2 border-foreground">
          <MapView
            center={loc}
            zoom={15}
            pickup={activeRide?.pickup}
            dropoff={activeRide?.dropoff}
            showRoute={!!activeRide}
            self={
              online && gpsStatus === "watching"
                ? {
                    location: loc,
                    picture: user?.picture,
                    initial: (user?.name?.[0] || profile?.vehicle_make?.[0] || "D").toUpperCase(),
                    speed_kmh: broadcastSpeed === false ? null : selfSpeed,
                    heading: selfHeading,
                    trail: selfTrail,
                    occupancy: occupancy,
                  }
                : null
            }
            followSelf={online && gpsStatus === "watching"}
            communityPins={communityPins}
            onMapClick={!activeRide && gpsStatus !== "watching" ? setMyLocation : undefined}
            dataTestId="driver-map"
          />
          {!activeRide && (
            <div className="absolute top-3 left-3 bg-background border-2 border-foreground px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] z-[400] flex items-center gap-2">
              {online && gpsStatus === "watching" ? (
                <>
                  <span className="inline-block w-2 h-2 bg-accent rounded-full pulse-dot" />
                  Live GPS tracking
                </>
              ) : (
                <>Tap map to update your location</>
              )}
            </div>
          )}
        </div>

        {/* Side panel */}
        <aside className="overflow-y-auto bg-card" data-testid="driver-panel">
          <div className="p-6">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-foreground/60">Driver</div>
            <h2 className="font-display text-2xl tracking-tighter">
              {profile.vehicle_color} {profile.vehicle_make} {profile.vehicle_model}
            </h2>
            <div className="mt-2 inline-block font-mono border-2 border-foreground px-2 py-1 text-sm" data-testid="driver-plate">
              {profile.vehicle_plate}
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4">
              <div className="border-2 border-foreground p-2">
                <div className="font-mono text-[10px] uppercase text-foreground/60">Trips</div>
                <div className="font-display text-lg" data-testid="driver-trips">{profile.total_trips}</div>
              </div>
              <div className="border-2 border-foreground p-2">
                <div className="font-mono text-[10px] uppercase text-foreground/60">Rating</div>
                <div className="font-display text-lg" data-testid="driver-rating">{profile.rating_avg || "—"}</div>
              </div>
              <div className="border-2 border-foreground p-2">
                <div className="font-mono text-[10px] uppercase text-foreground/60">Status</div>
                <div className="font-display text-sm uppercase">{profile.status}</div>
              </div>
            </div>

            <div className="mt-4 border-2 border-foreground p-3 flex items-center justify-between bg-background">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">Public broadcast</div>
                <div className="text-sm">
                  {broadcast
                    ? "Visible on landing-page live map"
                    : "Hidden from landing-page map"}
                </div>
              </div>
              <Switch
                data-testid="driver-broadcast-toggle"
                checked={broadcast}
                onCheckedChange={toggleBroadcast}
                className="data-[state=checked]:bg-accent"
              />
            </div>

            <div className="mt-2 border-2 border-foreground p-3 flex items-center justify-between bg-background">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">Broadcast speed</div>
                <div className="text-sm">
                  {broadcastSpeed
                    ? "Live km/h shown on driver popup"
                    : "Speed hidden — popup shows ‘Hidden’"}
                </div>
              </div>
              <Switch
                data-testid="driver-broadcast-speed-toggle"
                checked={broadcastSpeed}
                onCheckedChange={toggleBroadcastSpeed}
                disabled={!broadcast}
                className="data-[state=checked]:bg-accent"
              />
            </div>

            {activeRide && (
              <div className="mt-6 border-2 border-foreground p-4 bg-background" data-testid="driver-active-ride">
                <div className="font-mono text-xs uppercase tracking-wider text-accent">
                  {activeRide.status === "accepted" ? "Heading to pickup" : activeRide.status === "in_progress" ? "On trip" : activeRide.status}
                </div>
                <div className="font-display text-3xl mt-1">{formatGYD(activeRide.fare_gyd)}</div>
                <div className="font-mono text-xs uppercase tracking-wider text-foreground/60 mt-1">
                  {activeRide.distance_km} km · Collect in cash
                </div>
                <div className="mt-4 space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <MapPin size={16} weight="fill" className="text-primary mt-0.5" />
                    <div>
                      <div className="font-mono text-[10px] uppercase text-foreground/60">Pickup</div>
                      <div>{activeRide.pickup.address}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Path size={16} weight="fill" className="mt-0.5" />
                    <div>
                      <div className="font-mono text-[10px] uppercase text-foreground/60">Drop-off</div>
                      <div>{activeRide.dropoff.address}</div>
                    </div>
                  </div>
                  <div className="text-sm">
                    <span className="font-mono text-[10px] uppercase text-foreground/60">Rider · </span>
                    {activeRide.rider_name}
                  </div>
                </div>
                <RiderProfileCard profile={activeRide.rider_profile} />

                {/* Turn-by-turn nav: open the driver's preferred app pre-routed
                    to the next waypoint (pickup if accepted, dropoff if on trip). */}
                {(() => {
                  const target = activeRide.status === "in_progress" ? activeRide.dropoff : activeRide.pickup;
                  if (!target) return null;
                  const lat = target.lat;
                  const lng = target.lng;
                  const isAccepted = activeRide.status === "accepted";
                  const gmaps = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
                  const waze = `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
                  return (
                    <div className="mt-4 border-2 border-foreground bg-secondary/40 p-3" data-testid="driver-navigate-card">
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mb-2">
                        Turn-by-turn · {isAccepted ? "to pickup" : "to drop-off"}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <a
                          data-testid="driver-navigate-google"
                          href={gmaps}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 h-10 px-3 bg-foreground text-background font-semibold border-2 border-foreground hover:bg-foreground/90"
                        >
                          <Path size={16} weight="fill" /> Google Maps
                        </a>
                        <a
                          data-testid="driver-navigate-waze"
                          href={waze}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 h-10 px-3 bg-background text-foreground font-semibold border-2 border-foreground hover:bg-secondary"
                        >
                          <Crosshair size={16} weight="fill" /> Waze
                        </a>
                      </div>
                    </div>
                  );
                })()}

                <div className="flex gap-2 mt-4">
                  {activeRide.status === "accepted" && (
                    <Button
                      data-testid="driver-start-btn"
                      onClick={start}
                      className="rounded-none bg-foreground text-background hover:bg-foreground/90"
                    >
                      <Play size={16} weight="fill" /> Start trip
                    </Button>
                  )}
                  {activeRide.status === "in_progress" && (
                    <Button
                      data-testid="driver-complete-btn"
                      onClick={complete}
                      className="rounded-none bg-accent text-white hover:bg-accent/90"
                    >
                      <CheckCircle size={16} weight="fill" /> Complete · Collect cash
                    </Button>
                  )}
                  <Button
                    data-testid="driver-cancel-btn"
                    onClick={cancel}
                    variant="outline"
                    className="rounded-none border-2 border-foreground"
                  >
                    <XCircle size={16} /> Cancel
                  </Button>
                </div>

                {/* Rate the rider — available any time after pickup */}
                {(activeRide.status === "in_progress" || activeRide.status === "accepted") && (
                  <RateRiderInline ride={activeRide} onRated={loadActive} />
                )}
              </div>
            )}

            {!activeRide && online && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-xs uppercase tracking-[0.3em] text-foreground/60">
                    Incoming requests · {requests.length}
                  </div>
                  <button
                    type="button"
                    data-testid="driver-sound-toggle"
                    onClick={() => {
                      const next = !soundOn;
                      setSoundOn(next);
                      if (next) { primeAudio(); playRingtone(); }
                      toast.success(next ? "Ringtone on" : "Ringtone muted");
                    }}
                    title={soundOn ? "Mute new-request ringtone" : "Unmute new-request ringtone"}
                    className={`font-mono text-[10px] uppercase tracking-wider border-2 border-foreground h-7 px-2 flex items-center gap-1 ${soundOn ? "bg-accent text-white" : "bg-background text-foreground/60"}`}
                  >
                    {soundOn ? <SpeakerHigh size={12} weight="fill" /> : <SpeakerSlash size={12} weight="fill" />}
                    {soundOn ? "Ring" : "Muted"}
                  </button>
                </div>
                <ul className="space-y-2">
                  {requests.map((r) => (
                    <li key={r.ride_id} className="border-2 border-foreground p-3 bg-background" data-testid={`driver-request-${r.ride_id}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-display text-xl">{formatGYD(r.fare_gyd)}</span>
                        <span className="font-mono text-xs uppercase tracking-wider">{r.distance_km} km</span>
                      </div>
                      <div className="text-sm mt-1 text-foreground/80 line-clamp-1">
                        {r.pickup.address} → {r.dropoff.address}
                      </div>
                      {r.pickup_distance_km != null && (
                        <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60 mt-1">
                          {r.pickup_distance_km} km to pickup
                        </div>
                      )}
                      <RiderProfileCard profile={r.rider_profile} compact />
                      <Button
                        data-testid={`driver-accept-${r.ride_id}`}
                        onClick={() => accept(r.ride_id)}
                        className="w-full mt-3 rounded-none bg-foreground text-background hover:bg-foreground/90"
                      >
                        <Car size={16} weight="fill" /> Accept
                      </Button>
                    </li>
                  ))}
                  {requests.length === 0 && (
                    <li className="text-sm text-foreground/60 border-2 border-dashed border-foreground/20 p-4">
                      No requests yet. Hang tight — we'll ping you when one comes in.
                    </li>
                  )}
                </ul>
              </div>
            )}

            {!online && !activeRide && (
              <div className="mt-6 border-2 border-dashed border-foreground/30 p-4 text-sm text-foreground/70">
                Flip the switch above to go online and start receiving ride requests.
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}


function RiderTrustBadge({ profile, size = "sm" }) {
  if (!profile) return null;
  const count = profile.rider_rating_count || 0;
  const avg = profile.rider_rating_avg || 0;
  const trips = profile.total_trips || 0;
  // New riders get a different chip so drivers know to give them a chance.
  if (count === 0 && trips === 0) {
    return (
      <span
        data-testid="rider-trust-badge-new"
        className={`inline-flex items-center gap-1 font-mono uppercase tracking-wider border border-foreground/40 bg-primary/10 ${size === "sm" ? "text-[9px] px-1 py-0.5" : "text-[10px] px-1.5 py-0.5"}`}
        title="First-time rider on RideGY"
      >
        New
      </span>
    );
  }
  return (
    <span
      data-testid="rider-trust-badge"
      className={`inline-flex items-center gap-1 font-mono uppercase tracking-wider border border-foreground bg-background ${size === "sm" ? "text-[9px] px-1 py-0.5" : "text-[10px] px-1.5 py-0.5"}`}
      title={`${count} rating${count === 1 ? "" : "s"} from drivers · ${trips} completed trip${trips === 1 ? "" : "s"}`}
    >
      <Star size={size === "sm" ? 9 : 10} weight="fill" className="text-primary" />
      {avg > 0 ? avg.toFixed(1) : "—"}
      <span className="opacity-60">·</span>
      <span>{trips} {trips === 1 ? "ride" : "rides"}</span>
    </span>
  );
}


function RiderProfileCard({ profile, compact = false }) {
  if (!profile) return null;
  const initial = (profile.name || "R").trim().charAt(0).toUpperCase();
  const waNumber = (profile.phone || "").replace(/[^0-9]/g, "");

  if (compact) {
    return (
      <div className="mt-2 pt-2 border-t border-foreground/20 flex items-center gap-2" data-testid="rider-card-compact">
        <Avatar picture={profile.picture} initial={initial} size={28} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold leading-tight flex items-center gap-1.5 truncate">
            {profile.name || "Rider"}
            {profile.verified && (
              <ShieldCheck size={12} weight="fill" className="text-accent shrink-0" />
            )}
            <RiderTrustBadge profile={profile} size="sm" />
          </div>
          {profile.pickup_note && (
            <div className="text-[11px] text-foreground/70 truncate">{profile.pickup_note}</div>
          )}
        </div>
        {profile.accessibility && (
          <WheelchairMotion size={14} weight="fill" className="text-primary shrink-0" title={profile.accessibility} />
        )}
      </div>
    );
  }

  return (
    <div className="mt-4 border-2 border-foreground p-3" data-testid="rider-card-full">
      <div className="flex items-center gap-3">
        <Avatar picture={profile.picture} initial={initial} size={48} />
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg leading-tight flex items-center gap-1.5 truncate flex-wrap">
            {profile.rider_id ? (
              <a
                href={`/r/${profile.rider_id}`}
                target="_blank"
                rel="noreferrer"
                data-testid="rider-card-profile-link"
                className="hover:underline"
              >
                {profile.name || "Rider"}
              </a>
            ) : (
              <span>{profile.name || "Rider"}</span>
            )}
            {profile.verified && (
              <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider bg-accent text-white px-1.5 py-0.5">
                <ShieldCheck size={10} weight="fill" /> Verified
              </span>
            )}
            <RiderTrustBadge profile={profile} size="md" />
          </div>
          {profile.phone && (
            <div className="font-mono text-[11px] text-foreground/70 mt-0.5">{profile.phone}</div>
          )}
        </div>
        {profile.phone && (
          <div className="flex gap-1">
            <a
              href={`tel:${profile.phone}`}
              data-testid="rider-card-call"
              className="h-8 w-8 border-2 border-foreground inline-flex items-center justify-center hover:bg-foreground hover:text-background"
              aria-label="Call rider"
            >
              <Phone size={14} weight="fill" />
            </a>
            {waNumber && (
              <a
                href={`https://wa.me/${waNumber}`}
                target="_blank"
                rel="noreferrer"
                data-testid="rider-card-whatsapp"
                className="h-8 w-8 border-2 border-foreground inline-flex items-center justify-center bg-accent text-white hover:bg-accent/90"
                aria-label="WhatsApp rider"
              >
                <WhatsappLogo size={14} weight="fill" />
              </a>
            )}
          </div>
        )}
      </div>

      {profile.bio && (
        <p className="mt-3 text-sm text-foreground/80 leading-snug">{profile.bio}</p>
      )}
      {profile.pickup_note && (
        <div className="mt-3 flex items-start gap-2 text-sm bg-primary/20 border-l-2 border-primary px-2 py-1.5">
          <MapPin size={14} weight="fill" className="text-foreground mt-0.5 shrink-0" />
          <span><span className="font-mono text-[10px] uppercase tracking-wider mr-1">Pickup note:</span>{profile.pickup_note}</span>
        </div>
      )}
      {profile.accessibility && (
        <div className="mt-2 flex items-start gap-2 text-sm">
          <WheelchairMotion size={14} weight="fill" className="text-primary mt-0.5 shrink-0" />
          <span><span className="font-mono text-[10px] uppercase tracking-wider mr-1">Accessibility:</span>{profile.accessibility}</span>
        </div>
      )}
      {(profile.emergency_name || profile.emergency_phone) && (
        <div className="mt-2 flex items-start gap-2 text-sm">
          <Warning size={14} weight="fill" className="text-destructive mt-0.5 shrink-0" />
          <span>
            <span className="font-mono text-[10px] uppercase tracking-wider mr-1">Emergency:</span>
            {profile.emergency_name || "—"} {profile.emergency_phone ? `· ${profile.emergency_phone}` : ""}
          </span>
        </div>
      )}
    </div>
  );
}

function Avatar({ picture, initial, size = 40 }) {
  const px = `${size}px`;
  if (picture) {
    return (
      <img
        src={picture}
        alt=""
        referrerPolicy="no-referrer"
        style={{ width: px, height: px }}
        className="rounded-full object-cover border-2 border-foreground shrink-0"
      />
    );
  }
  return (
    <div
      style={{ width: px, height: px, fontSize: Math.round(size * 0.4) }}
      className="rounded-full bg-foreground text-background border-2 border-foreground inline-flex items-center justify-center font-display shrink-0"
    >
      {initial}
    </div>
  );
}



function RateRiderInline({ ride, onRated }) {
  const [rating, setRating] = useState(ride?.rider_rating || 0);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(!!ride?.rider_rating);

  if (done) {
    return (
      <div className="mt-4 border-2 border-accent bg-accent/10 p-3" data-testid="rate-rider-done">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/70">Your rating</div>
        <div className="flex items-center gap-1 mt-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} size={18} weight={i <= rating ? "fill" : "regular"} className="text-primary" />
          ))}
        </div>
      </div>
    );
  }

  const submit = async () => {
    if (!rating) return;
    setSubmitting(true);
    try {
      await api.post(`/rides/${ride.ride_id}/rate-rider`, { rating, note: note || null });
      setDone(true);
      toast.success(`Rider rated ${rating} ★`);
      onRated && onRated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save rating");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-4 border-2 border-foreground p-3" data-testid="rate-rider-inline">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60">Rate this rider</div>
      <div className="flex items-center gap-2 mt-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <button
            key={i}
            type="button"
            data-testid={`rate-rider-star-${i}`}
            onClick={() => setRating(i)}
            className="hover:scale-110 transition-transform"
            aria-label={`${i} stars`}
          >
            <Star size={28} weight={i <= rating ? "fill" : "regular"} className="text-primary" />
          </button>
        ))}
      </div>
      <textarea
        data-testid="rate-rider-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Optional note (kept private — only shown to admins)"
        maxLength={400}
        rows={2}
        className="mt-2 w-full rounded-none border-2 border-foreground p-2 text-sm bg-background"
      />
      <Button
        data-testid="rate-rider-submit"
        onClick={submit}
        disabled={!rating || submitting}
        className="mt-2 rounded-none bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50"
      >
        {submitting ? "Saving…" : "Submit rating"}
      </Button>
    </div>
  );
}


// ---------- Occupancy quick-set bar ----------
// Row of 6 chips a driver taps while waiting at a car park to broadcast how
// many seats are filled. The chosen value alternates with the driver's
// avatar on every map (rider, driver-self, public landing) every 2 seconds.
function OccupancyBar({ occupancy, onChange }) {
  const options = [
    { key: "empty", label: "Empty",   color: "#00A859", short: "0" },
    { key: "1",     label: "1 pax",   color: "#1d4ed8", short: "1" },
    { key: "2",     label: "2 pax",   color: "#1d4ed8", short: "2" },
    { key: "3",     label: "3 pax",   color: "#FF8800", short: "3" },
    { key: "4",     label: "4 pax",   color: "#FF8800", short: "4" },
    { key: "full",  label: "Full",    color: "#E11D48", short: "F" },
  ];
  return (
    <div
      data-testid="driver-occupancy-bar"
      className="border-b-2 border-foreground bg-card px-3 sm:px-6 py-2 flex items-center gap-2 overflow-x-auto"
    >
      <div className="shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/70">
        Occupancy
      </div>
      {options.map((o) => {
        const active = occupancy === o.key;
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onChange(o.key)}
            data-testid={`driver-occupancy-${o.key}`}
            className={`shrink-0 inline-flex items-center gap-1.5 h-8 px-2.5 border-2 border-foreground font-mono text-[11px] uppercase tracking-wider transition-colors ${
              active ? "text-white shadow-[2px_2px_0_#111]" : "bg-background hover:bg-foreground/5"
            }`}
            style={active ? { backgroundColor: o.color } : {}}
            title={o.label}
          >
            <span className="font-display text-sm leading-none">{o.short}</span>
            <span className="hidden sm:inline">{o.label}</span>
          </button>
        );
      })}
      {occupancy && (
        <button
          type="button"
          onClick={() => onChange(null)}
          data-testid="driver-occupancy-clear"
          className="ml-auto shrink-0 h-8 px-2 border-2 border-foreground bg-background hover:bg-destructive hover:text-destructive-foreground hover:border-destructive font-mono text-[10px] uppercase tracking-wider"
          title="Clear occupancy"
        >
          Clear
        </button>
      )}
    </div>
  );
}
